import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { apiClient } from '@indipia/shared'
import { PhotoViewer, TextLink, type PhotoViewerItem } from '@indipia/ui-core'
import type { ProgressPhoto } from '../../api/taskProgressApi'

// 읽기 전용 사진 표시 — 썸네일을 나열하지 않고 '사진 N장 ›' 링크로 PhotoViewer 를 연다(N5 대체안).
// 사진 url 은 인증이 필요한 게이트웨이 경로(/api/worklog/...)라 apiClient 로 blob 을 받아 objectURL 로 보여 준다
// (ThumbImg 와 같은 방식). 링크를 누를 때 받고, 닫으면 objectURL 을 모두 해제한다.
// 인디포토 출처만 있어 url 이 없는 사진은 열 수 없다 — 열 수 있는 사진이 하나도 없으면 링크가 아니라 글자로만 표시한다.

interface Props {
  photos: ProgressPhoto[]
  /** PhotoViewer 제목 */
  title: string
  /** 사진 대체 설명(예: '작업요청 사진') */
  alt: string
  /** 서버가 센 전체 장수(photos 길이와 다르면 이 값을 표기) */
  count?: number
  /** 부모 Modal 이 PhotoViewer 가 열린 동안 Esc 로 닫히지 않도록 열림 상태를 알린다 */
  onViewerOpenChange?: (open: boolean) => void
}

export function PhotoCountLink({ photos, title, alt, count, onViewerOpenChange }: Props) {
  const total = count ?? photos.length
  const viewable = photos.filter(p => !!p.url)
  const [items, setItems] = useState<PhotoViewerItem[] | null>(null)
  const [index, setIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const created = useRef<string[]>([])
  const aliveRef = useRef(true)
  const notifyRef = useRef(onViewerOpenChange)
  notifyRef.current = onViewerOpenChange

  const release = () => {
    created.current.forEach(u => URL.revokeObjectURL(u))
    created.current = []
  }
  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false; release() }
  }, [])

  if (total <= 0) return null
  if (viewable.length === 0) return <span>{`사진 ${total}장`}</span>

  const open = async () => {
    if (loading) return
    setLoading(true)
    const next: PhotoViewerItem[] = []
    let failed = 0
    for (let i = 0; i < viewable.length; i++) {
      try {
        // 서버 url 은 게이트웨이 기준 전체경로(/api/...)라 apiClient.baseURL 과 겹치는 선행 /api 를 뗀다.
        const path = (viewable[i].url as string).replace(/^\/api(?=\/)/, '')
        const res = await apiClient.get<Blob>(path, { responseType: 'blob' })
        if (!aliveRef.current) return
        const url = URL.createObjectURL(res.data)
        created.current.push(url)
        next.push({ id: i, src: url, alt: `${alt} ${i + 1}` })
      } catch {
        failed += 1
      }
    }
    if (!aliveRef.current) return
    setLoading(false)
    if (next.length === 0) { toast.error('사진을 불러오지 못했어요'); return }
    if (failed > 0) toast.error('일부 사진은 불러오지 못했어요')
    setIndex(0)
    setItems(next)
    notifyRef.current?.(true)
  }

  const close = () => {
    setItems(null)
    release()
    notifyRef.current?.(false)
  }

  return (
    <>
      <TextLink onClick={() => { void open() }} disabled={loading} aria-busy={loading}>
        {loading ? '사진 불러오는 중…' : `사진 ${total}장 ›`}
      </TextLink>
      {items && <PhotoViewer open onClose={close} items={items} index={index} onIndexChange={setIndex} title={title} />}
    </>
  )
}
